import { useEffect, useRef } from 'react';
import { GLYPH_SIZE, NEUTRAL_COLOR, effectiveIcon, resolveIcon, type MapIcon, type MapMarker, type MarkerCategory, type ResolvedIcon } from '@realmery/realm-core';

/**
 * Pins as the desktop draws them (`MapExport.svg`, `AtlasPins`): a teardrop in the pin's
 * category colour with a white edge and its icon in white, or a round token; GM-only ones
 * faded and dashed. Drawn at a fixed size on screen, the point of the teardrop on the place.
 */

/** The teardrop, 26 wide and 35 tall, its point at (0, 0) (`MapExport.PIN_PATH`). */
export const PIN_PATH = 'M0,0 C-2.5,-6 -13,-12 -13,-22 A13,13 0 1,1 13,-22 C13,-12 2.5,-6 0,0 Z';
export const TOKEN_RADIUS = 17;

/** `java.awt.Color.darker()` twice, as `MapExport.darker`: each channel × 0.7, dropping the fraction, each time. */
export function darker(hex: string): string {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i.exec(hex);
  if (!m) return '#000000';
  const once = (c: number) => Math.max(Math.trunc(c * 0.7), 0);
  return `#${m
    .slice(1, 4)
    .map((h) => once(once(parseInt(h, 16))).toString(16).padStart(2, '0'))
    .join('')}`;
}

/** Pictures as URLs to draw, made once each and let go when the page is. */
export function useIconUrls(): (icon: ResolvedIcon & { kind: 'PICTURE' }) => string {
  const made = useRef(new Map<string, string>());
  useEffect(
    () => () => {
      for (const url of made.current.values()) URL.revokeObjectURL(url);
      made.current.clear();
    },
    [],
  );
  return (icon) => {
    let url = made.current.get(icon.id);
    if (!url) {
      url = URL.createObjectURL(new Blob([icon.data as Uint8Array<ArrayBuffer>], { type: icon.mimeType || 'image/png' }));
      made.current.set(icon.id, url);
    }
    return url;
  };
}

export interface PinLook {
  readonly color: string;
  readonly icon: ResolvedIcon | null;
}

export function pinLook(marker: MapMarker, categories: ReadonlyMap<string, MarkerCategory>, icons: ReadonlyMap<string, MapIcon>): PinLook {
  const category = marker.categoryId ? categories.get(marker.categoryId) : undefined;
  return { color: category?.colorHex ?? NEUTRAL_COLOR, icon: resolveIcon(effectiveIcon(marker.icon, category?.icon ?? ''), (id) => icons.get(id)) };
}

/** An icon in white, `size` across, centred on (cx, cy) (`MapExport.svgIcon`); a white dot for none. */
export function IconArt({ icon, cx, cy, size, clipId, pictureUrl, fill = '#ffffff' }: { icon: ResolvedIcon | null; cx: number; cy: number; size: number; clipId: string; pictureUrl: (icon: ResolvedIcon & { kind: 'PICTURE' }) => string; fill?: string }) {
  if (!icon) return <circle cx={cx} cy={cy} r={4.5} fill={fill} />;
  switch (icon.kind) {
    case 'GLYPH':
      return <path d={icon.path} fill={fill} fillRule={icon.evenOdd ? 'evenodd' : undefined} transform={`translate(${cx - size / 2},${cy - size / 2}) scale(${size / GLYPH_SIZE})`} />;
    case 'PICTURE':
      return (
        <>
          <clipPath id={clipId}>
            <circle cx={cx} cy={cy} r={size / 2} />
          </clipPath>
          <image href={pictureUrl(icon)} x={cx - size / 2} y={cy - size / 2} width={size} height={size} clipPath={`url(#${clipId})`} preserveAspectRatio="xMidYMid slice" />
        </>
      );
    case 'TEXT':
      return (
        <text x={cx} y={cy + 4.5} fontSize={13} textAnchor="middle" fill={fill}>
          {icon.text}
        </text>
      );
  }
}

/** One pin, its point at (0, 0) of the group it's drawn in. */
export function PinArt({ marker, look, pictureUrl, selected }: { marker: Pick<MapMarker, 'id' | 'style' | 'visible'>; look: PinLook; pictureUrl: (icon: ResolvedIcon & { kind: 'PICTURE' }) => string; selected: boolean }) {
  const dash = marker.visible ? undefined : '3 2';
  const clip = `pin-${marker.id.replace(/[^A-Za-z0-9_-]/g, '')}`;
  if (marker.style === 'TOKEN') {
    const picture = look.icon?.kind === 'PICTURE';
    return (
      <>
        {selected && <circle r={TOKEN_RADIUS + 5} className="atlas-pin-ring" />}
        <circle r={TOKEN_RADIUS} fill={look.color} stroke="#ffffff" strokeWidth={1.5} strokeDasharray={dash} />
        {!picture && <circle r={TOKEN_RADIUS - 3} fill={darker(look.color)} />}
        <IconArt icon={look.icon} cx={0} cy={0} size={picture ? (TOKEN_RADIUS - 3) * 2 : (TOKEN_RADIUS - 3) * 1.35} clipId={clip} pictureUrl={pictureUrl} />
      </>
    );
  }
  return (
    <>
      {selected && <circle cy={-22} r={18} className="atlas-pin-ring" />}
      <path d={PIN_PATH} fill={look.color} stroke="#ffffff" strokeWidth={1.5} strokeDasharray={dash} />
      <IconArt icon={look.icon} cx={0} cy={-22} size={look.icon?.kind === 'PICTURE' ? 23 : 16} clipId={clip} pictureUrl={pictureUrl} />
    </>
  );
}

/** A category's badge for lists: a disc in its colour with its icon (`AtlasPins.categoryBadge`). */
export function CategoryBadge({ category, icons, pictureUrl, size = 18 }: { category: MarkerCategory | undefined; icons: ReadonlyMap<string, MapIcon>; pictureUrl: (icon: ResolvedIcon & { kind: 'PICTURE' }) => string; size?: number }) {
  const icon = category ? resolveIcon(effectiveIcon('', category.icon), (id) => icons.get(id)) : null;
  return (
    <svg width={size} height={size} viewBox="-12 -12 24 24" aria-hidden="true" className="atlas-badge">
      <circle r={12} fill={category?.colorHex ?? NEUTRAL_COLOR} />
      <IconArt icon={icon} cx={0} cy={0} size={15} clipId={`badge-${category?.id ?? 'none'}`.replace(/[^A-Za-z0-9_-]/g, '')} pictureUrl={pictureUrl} />
    </svg>
  );
}
